export interface StaticRouteStop { id: string; name: string; lon: number; lat: number }
export interface StaticRoutePattern {
  route: string;
  headsign: string;
  direction: number;
  tripId?: string;
  stops: StaticRouteStop[];
  path?: [number, number][];
}

let shapeIndexPromise: Promise<Record<string, string>> | undefined;
const shapeCache = new Map<string, Array<{ route: string; headsign: string; direction: number; tripId?: string; path: [number, number][] }>>();

const patternsCache = new Map<string, Promise<StaticRoutePattern[]>>();

function isRailRoute(route: string): boolean {
  const u = route.trim().toLocaleUpperCase();
  return /^M\d/.test(u) || /^L\d/.test(u) || /^T\d/.test(u)
    || /^(CCN|SHL|SCO|BMT|CTY|NSN|WST|ESI|IWL|APS|OLY|CMB|NTH|RTTA)/.test(u);
}

/**
 * Load route patterns for a given route. Rail routes (train/metro/light rail)
 * load a small dedicated file (~350 KB) instead of the full 20 MB bus-heavy
 * dataset, so tapping a train/bus vehicle is fast. Bus routes fall back to the
 * full file. Results are cached per file.
 */
function loadPatterns(route?: string): Promise<StaticRoutePattern[]> {
  const file = route && isRailRoute(route) ? '/data/route-patterns-rail.json' : '/data/route-patterns.json';
  let promise = patternsCache.get(file);
  if (!promise) {
    promise = fetch(file, { cache: 'force-cache' })
      .then(response => {
        if (!response.ok) throw new Error(`Static route data unavailable (${response.status})`);
        return response.json();
      })
      .then(data => data.patterns || []);
    patternsCache.set(file, promise);
  }
  return promise;
}

function loadShapeIndex(): Promise<Record<string, string>> {
  shapeIndexPromise ||= fetch('/data/route-shapes-index.json', { cache: 'force-cache' })
    .then(response => response.ok ? response.json() : { routes: {} })
    .then(data => data.routes || {});
  return shapeIndexPromise;
}

function patternKey(pattern: Pick<StaticRoutePattern, 'route' | 'headsign' | 'direction'>): string {
  return `${pattern.route}|${pattern.direction}|${pattern.headsign.toLocaleLowerCase()}`;
}

async function withShape(pattern: StaticRoutePattern): Promise<StaticRoutePattern> {
  if (pattern.path?.length) return pattern;
  const index = await loadShapeIndex();
  const file = index[pattern.route];
  if (!file) return pattern;
  let shapes = shapeCache.get(pattern.route);
  if (!shapes) {
    try {
      const response = await fetch(`/data/route-shapes/${file}`, { cache: 'force-cache' });
      if (!response.ok) return pattern;
      const loadedShapes = (await response.json()).shapes || [];
      shapes = loadedShapes;
      shapeCache.set(pattern.route, loadedShapes);
    } catch { return pattern; }
  }
  const shape = shapes?.find(candidate => patternKey(candidate) === patternKey(pattern));
  return shape?.path?.length ? { ...pattern, path: shape.path } : pattern;
}

/**
 * Load the polyline paths for a set of public route codes (e.g. T1, M1, L1).
 * Returns a map of route code → array of [lon, lat] paths (one per direction).
 * Used by the live map to draw the rail/metro/light-rail network as coloured
 * lines. Shapes are cached in memory so repeated calls are cheap.
 */
export async function loadRouteShapes(routes: string[]): Promise<Map<string, [number, number][][]>> {
  const index = await loadShapeIndex();
  const result = new Map<string, [number, number][][]>();
  const unique = [...new Set(routes.map(r => r.toLocaleUpperCase()).filter(Boolean))];
  await Promise.all(unique.map(async (route) => {
    const file = index[route];
    if (!file) return;
    let shapes = shapeCache.get(route);
    if (!shapes) {
      try {
        const response = await fetch(`/data/route-shapes/${file}`, { cache: 'force-cache' });
        if (!response.ok) return;
        const loaded = (await response.json()).shapes || [];
        shapeCache.set(route, loaded);
        shapes = loaded;
      } catch { return; }
    }
    const paths = (shapes || []).map(s => s.path).filter((p): p is [number, number][] => !!p?.length);
    if (paths.length) result.set(route, paths);
  }));
  return result;
}

function words(value: string): Set<string> {
  return new Set(value.toLocaleLowerCase().replace(/\b(via|station|stand|platform|the)\b/g, ' ').match(/[a-z0-9]+/g) || []);
}

function overlap(a: string, b: string): number {
  const aa = words(a); const bb = words(b);
  let score = 0;
  for (const word of aa) if (bb.has(word)) score++;
  return score;
}

export function canonicalRoute(value: string): string {
  const upper = value.trim().toLocaleUpperCase();
  // Some realtime feeds use agency-specific route ids rather than the
  // passenger-facing line number. Keep these aliases in one place so both
  // static pattern matching and live vehicle inference use the same identity.
  if (/^IWLR(?:[-_]|$)/.test(upper)) return 'L1';
  if (/^CSELR(?:[-_]|$)/.test(upper)) return 'L2';
  if (/^(?:IWLR|191)(?:[-_]|$)/.test(upper)) return 'L1';
  const compactModeCode = upper.match(/^\d*(?:L|F|T|M)\d+[A-Z]?$/)?.[0];
  if (compactModeCode) return compactModeCode.replace(/^\d+/, '');
  const prefixedModeCode = upper.match(/^\d+((?:L|F|T|M)\d+[A-Z]?)(?:[-_]|$)/)?.[1];
  if (prefixedModeCode) return prefixedModeCode;
  const trainPrefix: Record<string, string> = {
    // Sydney suburban lines — GTFS route_id prefixes → public line codes
    NTH: 'T9',   // Northern Line
    NSN: 'T1',   // North Shore Line
    WST: 'T1',   // Western Line
    ESI: 'T4',   // Eastern Suburbs & Illawarra Line
    IWL: 'T2',   // Inner West Line
    APS: 'T8',   // Airport Line
    OLY: 'T7',   // Olympic Park Line
    CMB: 'T5',   // Cumberland Line
    BMT: 'T1',   // Blue Mountains Line (runs on the T1 corridor within Sydney)
    // Regional/intercity lines — keep their recognised codes
    CCN: 'CCN',  // Central Coast & Newcastle Line
    SHL: 'SHL',  // Southern Highlands Line
    SCO: 'SCO',  // South Coast Line
    CTY: 'CTY',  // City Circle
    RTTA: 'RTTA',// Special services
  };
  const prefix = upper.split('_')[0];
  if (trainPrefix[prefix]) return trainPrefix[prefix];
  const linePrefix = prefix.match(/^(?:T|L|F|M)\d+[A-Z]?/);
  if (linePrefix) return linePrefix[0];
  // GTFS ids such as `9-F8-sj2-1` and `80L3-...` embed the public line
  // token after an agency/series prefix. Prefer that token to a leading
  // numeric fragment (which otherwise turns into `9` or `80L`).
  const prefixedEmbeddedLine = upper.match(/(?:^|[-_])\d+((?:T|L|F|M)\d+[A-Z]?)(?:$|[-_])/);
  if (prefixedEmbeddedLine) return prefixedEmbeddedLine[1];
  const embeddedLine = upper.match(/(?:^|[-_])((?:T|L|F|M)\d+[A-Z]?)(?:$|[-_])/);
  if (embeddedLine) return embeddedLine[1];
  if (/^[A-Z]{2,4}$/.test(prefix)) return prefix;
  if (upper.includes('_')) return upper.split('_').at(-1) || upper;
  const line = upper.split('-').find(part => /^[TNFLM]\d+[A-Z]?$/.test(part));
  return upper.match(/^[A-Z]*\d+[A-Z]?/)?.[0] || line || upper;
}

function stopKey(name: string): string {
  return name.replace(/,\s*(Platform|Stand)\s+[^,]+.*$/i, '').replace(/\s+/g, ' ').trim().toLocaleLowerCase();
}

function bestNode(query: string, nodes: Map<string, StaticRouteStop>): string | undefined {
  const best = [...nodes].map(([key, stop]) => ({ key, score: overlap(query, stop.name) }))
    .sort((a, b) => b.score - a.score)[0];
  return best?.score ? best.key : undefined;
}

function networkPath(patterns: StaticRoutePattern[], origin: string, destination: string): StaticRouteStop[] | undefined {
  const nodes = new Map<string, StaticRouteStop>();
  const edges = new Map<string, Set<string>>();
  const connect = (a: string, b: string) => {
    if (!edges.has(a)) edges.set(a, new Set());
    edges.get(a)!.add(b);
  };
  for (const pattern of patterns) for (let i = 0; i < pattern.stops.length; i++) {
    const stop = pattern.stops[i]; const key = stopKey(stop.name);
    nodes.set(key, stop);
    if (i > 0) {
      const previous = stopKey(pattern.stops[i - 1].name);
      connect(previous, key); connect(key, previous);
    }
  }
  const destinationName = destination.split(/\s+via\s+/i)[0];
  const viaName = destination.match(/\s+via\s+(.+)$/i)?.[1];
  const start = bestNode(origin, nodes); const end = bestNode(destinationName, nodes);
  const via = viaName ? bestNode(viaName, nodes) : undefined;
  if (!start || !end) return undefined;

  const find = (from: string, to: string): string[] | undefined => {
    const queue = [from]; const previous = new Map<string, string | null>([[from, null]]);
    for (let cursor = 0; cursor < queue.length; cursor++) {
      const current = queue[cursor];
      if (current === to) break;
      for (const next of edges.get(current) || []) if (!previous.has(next)) {
        previous.set(next, current); queue.push(next);
      }
    }
    if (!previous.has(to)) return undefined;
    const path: string[] = [];
    for (let current: string | null = to; current; current = previous.get(current) || null) path.push(current);
    return path.reverse();
  };
  const first = find(start, via || end);
  const second = via ? find(via, end) : undefined;
  const keys = first && (via ? second ? [...first, ...second.slice(1)] : undefined : first);
  return keys?.map(key => nodes.get(key)!).filter(Boolean);
}

function distanceKm(a: [number, number], b: [number, number]): number {
  const toRad = Math.PI / 180;
  const dLat = (b[1] - a[1]) * toRad;
  const dLon = (b[0] - a[0]) * toRad;
  const lat1 = a[1] * toRad; const lat2 = b[1] * toRad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(h));
}

export async function findStaticRoutePattern(
  route: string,
  origin: string,
  destination: string,
  currentCoords?: [number, number],
): Promise<StaticRoutePattern | undefined> {
  const candidates = (await loadPatterns(route)).filter(pattern => canonicalRoute(pattern.route) === canonicalRoute(route));
  if (!candidates.length) return undefined;
  const destinationName = destination.split(/\s+via\s+/i)[0].trim();
  const viaName = destination.match(/\s+via\s+(.+)$/i)?.[1]?.trim();
  const ranked = candidates.map(pattern => {
    const nearest = currentCoords
      ? Math.min(...pattern.stops.map(stop => distanceKm(currentCoords, [stop.lon, stop.lat])))
      : Number.POSITIVE_INFINITY;
    const nearestIndex = currentCoords
      ? pattern.stops.reduce((bestIndex, stop, index) => distanceKm(currentCoords, [stop.lon, stop.lat]) < distanceKm(currentCoords, [pattern.stops[bestIndex].lon, pattern.stops[bestIndex].lat]) ? index : bestIndex, 0)
      : -1;
    const namedIndex = origin ? pattern.stops.findIndex(stop => overlap(origin, stop.name) > 0) : -1;
    const currentIndex = namedIndex >= 0 ? namedIndex : (nearest < 0.75 ? nearestIndex : -1);
    const destinationIndex = pattern.stops.findIndex(stop => overlap(destinationName, stop.name) > 0);
    const viaIndex = viaName ? pattern.stops.findIndex(stop => overlap(viaName, stop.name) > 0) : -1;
    const headsignMatches = overlap(destinationName, pattern.headsign) > 0;
    const destinationStopMatches = destinationIndex >= 0;
    const endsCorrectly = headsignMatches || overlap(destinationName, pattern.stops.at(-1)?.name || '') > 0;
    const ordered = currentIndex < 0 || destinationIndex < 0 || destinationIndex >= currentIndex;
    const viaOrdered = !viaName || viaIndex < 0 || currentIndex < 0 || viaIndex >= currentIndex;

    // A route pattern is the complete scheduled run, not just the remaining
    // portion after the selected stop. Realtime destinations often use a
    // different wording (for example “Gordon via Central”), so rank any
    // pattern containing the current stop and matching the destination before
    // considering direction/order. This keeps the upstream stops visible and
    // avoids selecting a short turnback merely because its headsign overlaps.
    const score = (destinationStopMatches ? 140 : 0) + (headsignMatches ? 100 : 0)
      + (currentIndex >= 0 ? 90 : 0) + (viaName && viaIndex >= 0 ? 30 : 0)
      + (ordered ? 20 : -5) + (viaOrdered ? 8 : -2)
      + Math.min(pattern.stops.length, 120) * 0.5
      - (Number.isFinite(nearest) ? nearest * 12 : 0);
    return { pattern, score, nearest, currentIndex, destinationIndex, viaIndex, endsCorrectly, ordered, viaOrdered };
  }).filter(candidate => (!currentCoords || candidate.nearest < 5)
    // If the selected stop is not in the API cache, retain candidates so the
    // destination/headsign ranking below can still choose a complete pattern.
    // Once coordinates are known, requiring the stop on the pattern prevents
    // an unrelated branch from being shown.
    && (!currentCoords || candidate.currentIndex >= 0))
    .sort((a, b) => b.score - a.score);
  const bestCandidate = ranked[0];
  const best = bestCandidate?.pattern;
  if (best && (bestCandidate.destinationIndex >= 0 || bestCandidate.endsCorrectly)) return withShape(best);
  if (best && !origin && !currentCoords) return withShape(best);
  const path = networkPath(candidates, origin, destination);
  return path?.length ? { route, headsign: destination, direction: 0, stops: path } : (best ? withShape(best) : undefined);
}

/** Resolve a realtime route id to its public route and likely destination. */
export async function inferVehicleRoute(routeId: string, coords: [number, number], mode?: string): Promise<{ route: string; destination: string } | undefined> {
  const candidates = (await loadPatterns(routeId)).filter(pattern => canonicalRoute(pattern.route) === canonicalRoute(routeId));
  let best: { pattern: StaticRoutePattern; distance: number } | undefined;
  // Prefer the canonical public pattern (L1/L2/F8/T9) over agency aliases
  // such as 80L3. The latter are useful as a fallback, but should never leak
  // into a passenger-facing vehicle label when a public pattern exists.
  const canonical = canonicalRoute(routeId);
  const preferred = candidates.filter(pattern => pattern.route.toLocaleUpperCase() === canonical);
  const rankedCandidates = preferred.length ? preferred : candidates;
  for (const pattern of rankedCandidates) {
    const distance = Math.min(...pattern.stops.map(stop => distanceKm(coords, [stop.lon, stop.lat])));
    if (!best || distance < best.distance) best = { pattern, distance };
  }
  // Ferry positions can be several kilometres from the nearest wharf while
  // still being unambiguously on that service, especially in open harbour
  // water. Rail and bus positions retain the tighter sanity bound.
  const maxDistance = mode === 'Ferry' || mode === 'Light Rail' ? 10 : 3;
  if (!best || best.distance > maxDistance) return undefined;
  return { route: canonical || best.pattern.route, destination: best.pattern.headsign || best.pattern.stops.at(-1)?.name || '' };
}

/**
 * Infer the direction of travel (bearing in degrees) for a vehicle whose feed
 * omits a bearing (the Sydney Trains feed does). Finds the nearest point on
 * the route's shape to the vehicle and returns the heading to the next shape
 * point. Returns undefined when no usable shape exists.
 */
export async function inferVehicleBearing(routeId: string, coords: [number, number]): Promise<number | undefined> {
  const canonical = canonicalRoute(routeId);
  const shapes = await loadRouteShapes([canonical]);
  const paths = shapes.get(canonical);
  if (!paths?.length) return undefined;
  const best = { path: paths[0], index: 0, distance: Number.POSITIVE_INFINITY };
  for (const path of paths) {
    for (let i = 0; i < path.length; i++) {
      const d = distanceKm(coords, path[i]);
      if (d < best.distance) { best.distance = d; best.path = path; best.index = i; }
    }
  }
  const next = best.path[best.index + 1];
  const prev = best.path[Math.max(best.index - 1, 0)];
  const target = next || prev;
  if (!target) return undefined;
  const dx = target[0] - coords[0];
  const dy = target[1] - coords[1];
  // Heading is the clockwise angle from north (0-360).
  let bearing = (Math.atan2(dx, dy) * 180) / Math.PI;
  if (bearing < 0) bearing += 360;
  return Math.round(bearing);
}
